import React, { useState, useCallback } from 'react';
import classNames from 'classnames';
import { Stack, ControlLink, Text, Checkbox, Button, Overlay, LoadingBalls } from '@moda/om';
import { useCart, CartVersion } from '../../hooks/useCart';
import { useErrorModal } from '../../hooks/useErrorModal';
import { useEnrollMutation } from '../../hooks/useEnroll';
import { reportError } from '../../lib/reportError';
import { useUser } from '../../hooks/useUser';
import { UserRewardStatus, UserStatusLevel, RewardModes } from '../../constants';
import { RewardPointsPreview } from './RewardPointsPreview';

import './RewardJoin.scss';

interface Props {
  theme?: 'box' | 'simple';
}

export const RewardJoin: React.FC<Props> = ({ theme = 'box' }) => {
  const { cart } = useCart();
  const { user } = useUser();
  const [checked, setChecked] = useState(true);
  const { handleEnroll, loading } = useEnrollMutation();
  const { showErrorModal } = useErrorModal();

  const [mode, setMode] = useState(
    user.statusLevel !== UserStatusLevel.Employee &&
      user.userLoyaltyStatus === UserRewardStatus.Enrolled
      ? RewardModes.Joined
      : RewardModes.PreJoin
  );

  const points = cart.attributes.rewardPoints;

  const joinRewards = useCallback(async () => {
    setMode(RewardModes.Saving);
    try {
      await handleEnroll();
      setMode(RewardModes.JustJoined);
    } catch (error) {
      setMode(RewardModes.PreJoin);
      showErrorModal();
      reportError('JOIN_REWARDS', error);
    }
  }, [handleEnroll, showErrorModal]);

  if (cart.attributes.version === CartVersion.MiniBag) return null;

  const viewRewardJoin =
    user.statusLevel === UserStatusLevel.Customer &&
    user.userLoyaltyStatus !== UserRewardStatus.NoAccess;

  if (!viewRewardJoin) return null;

  return (
    <>
      {mode === RewardModes.Joined && <RewardPointsPreview />}
      {mode !== RewardModes.Joined && (
        <Stack
          space={2}
          className={classNames('RewardJoin', `RewardJoin--${theme}`)}
          data-testid="reward-join"
        >
          {mode === RewardModes.PreJoin && (
            <>
              <Stack space={2}>
                <Text treatment="body1">Join Moda Rewards to earn</Text>
                <Text treatment="bold1">{` ${points} points`}</Text>
              </Stack>
              <Stack direction="horizontal" space={2}>
                <Text>
                  Plus, get +500 points just for joining!{' '}
                  <ControlLink to="/rewards" target="_blank">
                    Learn More
                  </ControlLink>
                </Text>
              </Stack>
              <Stack direction="horizontal" space={2}>
                <Checkbox
                  className="RewardJoin__checkbox"
                  defaultChecked
                  id="agreeToTerms"
                  onChange={event => setChecked(event.target.checked)}
                />
                <Text>
                  I agree to the{' '}
                  <ControlLink to="/terms" target="_blank">
                    Terms &amp; Conditions
                  </ControlLink>
                </Text>
              </Stack>
              <Button
                className="RewardJoin__button"
                secondary
                disabled={!checked}
                aria-label="Join Rewards"
                onClick={() => joinRewards()}
              >
                JOIN REWARDS
              </Button>
            </>
          )}

          {mode === RewardModes.JustJoined && (
            <Text treatment="body1">{`Thanks for signing up for Moda Rewards! You will earn ${points} points from this purchase.`}</Text>
          )}

          <Overlay show={mode === RewardModes.Saving}>
            <LoadingBalls />

            <Text treatment="eyebrow">Saving</Text>
          </Overlay>

          <Overlay show={loading}>
            <LoadingBalls />
          </Overlay>
        </Stack>
      )}
    </>
  );
};
